import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, NodeKey, NodeMutation } from 'lexical';
import { useEffect, useRef } from 'react';
import {
  $isSuggestionMarkNode,
  $sourceSuggestionMark,
  SuggestionMarkNode,
} from '../nodes/SuggestionMarkNode';
import { $isSuggestionNode, $sourceSuggestion, SuggestionNode } from '../nodes/SuggestionNode';
import { Source } from '../types';
import { scrollToNode } from '../utils/scrollToNode';

type TrackedSuggestion = {
  key: NodeKey;
  source: Source;
  textContent: string;
  scrollIntoView: () => void;
};

export type GroupedSuggestions = Record<Source, Record<NodeKey, TrackedSuggestion>>;

type Props = {
  onSuggestionsChange: (suggestions: GroupedSuggestions) => void;
};

export function SuggestionTrackerPlugin({ onSuggestionsChange }: Props) {
  const [editor] = useLexicalComposerContext();
  const onSuggestionsChangeRef = useRef(onSuggestionsChange);

  useEffect(() => {
    onSuggestionsChangeRef.current = onSuggestionsChange;
  }, [onSuggestionsChange]);

  useEffect(() => {
    const grouped: GroupedSuggestions = {
      default: {},
      note: {},
      inline: {},
      placeholder: {},
      deletion: {},
    };

    function removeFromGrouped(key: NodeKey) {
      delete grouped.default[key];
      delete grouped.note[key];
      delete grouped.inline[key];
      delete grouped.placeholder[key];
      delete grouped.deletion[key];
    }

    function handleMutations(mutations: Map<NodeKey, NodeMutation>) {
      editor.getEditorState().read(() => {
        for (const [key, type] of mutations) {
          if (type === 'destroyed') {
            removeFromGrouped(key);
          } else if (type === 'created' || type === 'updated') {
            if (type === 'updated') removeFromGrouped(key);

            const node = $getNodeByKey(key);
            let source: Source | null = null;
            if ($isSuggestionNode(node)) {
              source = $sourceSuggestion(node);
            } else if ($isSuggestionMarkNode(node)) {
              source = $sourceSuggestionMark(node);
            }
            if (source && node) {
              grouped[source][key] = {
                key,
                source,
                textContent: node.getTextContent(),
                scrollIntoView: () => scrollToNode(editor, key),
              };
            }
          }
        }

        onSuggestionsChangeRef.current({
          default: { ...grouped.default },
          note: { ...grouped.note },
          inline: { ...grouped.inline },
          placeholder: { ...grouped.placeholder },
          deletion: { ...grouped.deletion },
        });
      });
    }

    const unregisterBlock = editor.registerMutationListener(SuggestionNode, handleMutations, {
      skipInitialization: false,
    });
    const unregisterMark = editor.registerMutationListener(SuggestionMarkNode, handleMutations, {
      skipInitialization: false,
    });

    return () => {
      unregisterBlock();
      unregisterMark();
    };
  }, [editor]);

  return null;
}
